/**
 * The sync engine (sync protocol §4).
 *
 * Joins the replica to the three endpoints and decides when to talk to them.
 * Nothing on the write path waits for the network: a local edit lands in the
 * replica and the outbox, the UI returns, and a push is scheduled behind it. That
 * is what makes NFR-PERF-1's 500 ms acknowledgment independent of connectivity.
 *
 * Pushing is deliberately eager rather than efficient. Batching would save a
 * trivial number of requests and widen the window in which unpushed events are the
 * only copy of someone's work -- risk R4, and the reason §4 says to push early.
 */
import type { AnyEvent, SupersededField } from '@intentionalist/shared';
import { PUSH_BATCH_MAX } from '@intentionalist/shared';
import type { Replica } from './replica.js';
import { bootstrapAll, pullAll, push as pushEvents } from './sync.js';
import { NotSignedIn } from './api.js';
import { upload } from './photos.js';

export interface SyncState {
  readonly status: 'idle' | 'syncing' | 'error';
  /** Unpushed events. FR-10.4 wants this visible rather than inferable. */
  readonly pending: number;
  readonly lastSyncAt: number | null;
  /** Plain language, no status codes (NFR-UX-8). */
  readonly error: string | null;
  /**
   * Local writes that lost a merge.
   *
   * Surfaced rather than discarded: the losing value is still in the log, and
   * NFR-REL-5 is only honestly satisfied if someone can be told.
   */
  readonly superseded: readonly SupersededField[];
  /**
   * The session ended while syncing.
   *
   * Reported rather than swallowed: the outbox is untouched and still has work in
   * it, so leaving the UI showing "idle" with changes pending would be a lie --
   * nothing will drain until the person signs in again.
   */
  readonly signedOut: boolean;
  /** Photos captured but not yet in S3. Separate from `pending`, per §4. */
  readonly photosPending: number;
}

/** The three calls, injectable so the engine can be tested without a network. */
export interface SyncTransport {
  push: typeof pushEvents;
  pullAll: typeof pullAll;
  bootstrapAll: typeof bootstrapAll;
}

const DEFAULT_TRANSPORT: SyncTransport = { push: pushEvents, pullAll, bootstrapAll };

/** §4: debounced about a second, so a burst of edits becomes one request. */
export const PUSH_DEBOUNCE_MS = 1000;
/** §4: a timer while foregrounded, in case nothing else triggers. */
export const PERIODIC_SYNC_MS = 5 * 60 * 1000;
/** §4: exponential backoff with jitter, capped at five minutes. */
export const MAX_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Timings, injectable so tests need not fake the clock.
 *
 * Faking timers is not an option here: IndexedDB schedules its own transaction
 * events on timers, so a test that fakes them deadlocks the database rather than
 * the engine.
 */
export interface SyncTimings {
  readonly pushDebounceMs: number;
  readonly periodicMs: number;
}

export class SyncEngine {
  private state: SyncState = {
    status: 'idle',
    pending: 0,
    lastSyncAt: null,
    error: null,
    superseded: [],
    signedOut: false,
    photosPending: 0,
  };

  private readonly listeners = new Set<(state: SyncState) => void>();
  private pushTimer: ReturnType<typeof setTimeout> | undefined;
  private periodic: ReturnType<typeof setInterval> | undefined;
  private failures = 0;
  private running = false;
  private detach: (() => void) | undefined;

  private readonly timings: SyncTimings;

  constructor(
    readonly replica: Replica,
    private readonly transport: SyncTransport = DEFAULT_TRANSPORT,
    timings: Partial<SyncTimings> = {},
  ) {
    this.timings = {
      pushDebounceMs: timings.pushDebounceMs ?? PUSH_DEBOUNCE_MS,
      periodicMs: timings.periodicMs ?? PERIODIC_SYNC_MS,
    };
  }

  subscribe(listener: (state: SyncState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => void this.listeners.delete(listener);
  }

  current(): SyncState {
    return this.state;
  }

  private async emit(patch: Partial<SyncState>): Promise<void> {
    /*
     * The pending count is a read, and the replica can close under it.
     *
     * Signing out closes the database while a sync may still be in flight, and
     * this is called from `syncNow`'s own catch -- so a throw here escapes that
     * catch entirely and surfaces as an unhandled rejection rather than as a
     * sync that failed. Keeping the previous count is right as well as quiet:
     * nothing has been pushed, so nothing about what is pending has changed.
     */
    let pending = this.state.pending;
    try {
      pending = await this.replica.pendingCount();
    } catch {
      // Closed, or closing. The state below is still worth publishing.
    }

    this.state = {
      ...this.state,
      ...patch,
      pending,
      photosPending: this.pendingPhotos().length,
    };
    for (const listener of this.listeners) listener(this.state);
  }

  /**
   * Prepares the replica for this user and catches up.
   *
   * Bootstraps when there is nothing local to pull from -- a first sign-in, or a
   * database that belonged to someone else and was cleared. Otherwise it pulls
   * from the stored cursor, which is far cheaper than replaying the whole log.
   */
  async start(userId: string): Promise<void> {
    await this.replica.db.open();
    const { wiped } = await this.replica.claim(userId);
    await this.replica.hydrate();

    const cursor = await this.replica.cursor();
    if (wiped || cursor === '') {
      await this.bootstrap();
    }

    this.listen();
    await this.syncNow();
  }

  /** Full state plus a cursor, for a device starting from nothing (case 11). */
  private async bootstrap(): Promise<void> {
    const { records, cursor } = await this.transport.bootstrapAll();
    await this.replica.loadBootstrap(
      records as ({ kind: string } & Record<string, unknown>)[],
      cursor,
    );
  }

  /**
   * Records local edits and schedules a push.
   *
   * Returns once the replica has them, never once the server has.
   */
  async write(events: readonly AnyEvent[]): Promise<SupersededField[]> {
    const superseded = await this.replica.write(events);
    if (superseded.length > 0) {
      await this.emit({ superseded: [...this.state.superseded, ...superseded] });
    }
    this.schedulePush();
    await this.emit({});
    return superseded;
  }

  private schedulePush(delay = this.timings.pushDebounceMs): void {
    clearTimeout(this.pushTimer);
    this.pushTimer = setTimeout(() => void this.syncNow(), delay);
  }

  /**
   * Pushes, then pulls (§4 pulls immediately after a successful push).
   *
   * Serialized: a second call while one is in flight returns rather than
   * interleaving two passes over the same outbox.
   */
  async syncNow(): Promise<void> {
    if (this.running) return;
    this.running = true;
    await this.emit({ status: 'syncing', error: null });

    try {
      await this.pushPending();
      await this.pullChanges();
      // After the events, and never before them: a `photo.uploaded` for a photo
      // whose `photo.added` has not reached the server would reference a photo
      // nothing else knows about.
      await this.uploadPhotos();
      this.failures = 0;
      await this.emit({ status: 'idle', lastSyncAt: Date.now(), error: null });
    } catch (error) {
      if (error instanceof NotSignedIn) {
        // Not a sync failure; the session ended. The outbox is untouched and will
        // go up after the next sign-in.
        await this.emit({ status: 'idle', signedOut: true });
        return;
      }
      this.failures += 1;
      await this.emit({
        status: 'error',
        error: error instanceof Error ? error.message : 'Could not reach the service.',
      });
      // Retry with backoff. The outbox is never cleared on failure, so nothing is
      // lost by waiting.
      this.schedulePush(this.backoff());
    } finally {
      this.running = false;
    }
  }

  /** Exponential with jitter, so several devices do not retry in lockstep. */
  private backoff(): number {
    const base = Math.min(2 ** this.failures * 1000, MAX_BACKOFF_MS);
    return Math.min(base / 2 + Math.random() * (base / 2), MAX_BACKOFF_MS);
  }

  private async pushPending(): Promise<void> {
    for (;;) {
      const batch = (await this.replica.pending()).slice(0, PUSH_BATCH_MAX);
      if (batch.length === 0) return;

      const response = await this.transport.push(batch.map((row) => row.event as AnyEvent));

      const settled = await this.replica.settle({
        accepted: response.accepted,
        duplicates: response.duplicates,
        // Permanently invalid: retrying would wedge everything behind them.
        rejected: response.rejected.map((r) => r.eventId).filter((id): id is string => !!id),
      });

      if (response.superseded.length > 0) {
        await this.emit({ superseded: [...this.state.superseded, ...response.superseded] });
      }

      if (settled === 0) {
        // Nothing was taken responsibility for, so pushing the same batch again
        // would loop. Count the attempt and let backoff handle it.
        await this.replica.noteAttempt(batch.map((row) => row.eventId));
        throw new Error('The service did not accept those changes.');
      }

      if (batch.length < PUSH_BATCH_MAX) return;
    }
  }

  private async pullChanges(): Promise<void> {
    const since = await this.replica.cursor();
    const { events, cursor } = await this.transport.pullAll(since);
    if (events.length === 0 && cursor === since) return;
    await this.replica.applyPulled(events as AnyEvent[], cursor);
  }

  /**
   * Photos that have been captured but not yet confirmed in S3.
   *
   * Read from the projection rather than a separate queue: `photo.added` with no
   * `photo.uploaded` *is* the queue, which means it survives a restart and an
   * eviction for free, and cannot drift from what the manifest says.
   */
  private pendingPhotos(): { itemId: string; photoId: string }[] {
    const waiting: { itemId: string; photoId: string }[] = [];
    for (const item of this.replica.manifest.items.values()) {
      for (const photo of item.photos.values()) {
        if (photo.lifecycle?.present === true && photo.uploaded?.v !== true) {
          waiting.push({ itemId: item.itemId, photoId: photo.photoId });
        }
      }
    }
    return waiting;
  }

  /**
   * Uploads what is waiting, one photo at a time.
   *
   * Sequential on purpose: a phone on a slow connection uploading ten photos at
   * once finishes none of them, and §4 wants each to become visible elsewhere as
   * soon as it can.
   *
   * A failure is recorded and does not stop the others -- one photo that cannot
   * be stored must not block the rest, and it is retried on the next sync.
   */
  private async uploadPhotos(): Promise<void> {
    const waiting = this.pendingPhotos();
    if (waiting.length === 0) return;

    for (const { itemId, photoId } of waiting) {
      const outcome = await upload({ db: this.replica.db, itemId, photoId });
      if (outcome.event !== null) {
        // Written through the normal path, so it queues for push like any edit.
        await this.replica.write([outcome.event]);
      }
      await this.emit({});
    }

    // Anything newly written needs sending; the push that already ran was before
    // these events existed.
    if ((await this.replica.pendingCount()) > 0) await this.pushPending();
  }

  /** §4's triggers: reconnecting, regaining focus, and a timer while foregrounded. */
  private listen(): void {
    if (this.detach !== undefined) return;

    const onOnline = () => {
      this.failures = 0;
      void this.syncNow();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.syncNow();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    this.periodic = setInterval(() => void this.syncNow(), this.timings.periodicMs);

    this.detach = () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(this.periodic);
    };
  }

  /** Acknowledges the superseded notices, once someone has seen them. */
  async dismissSuperseded(): Promise<void> {
    await this.emit({ superseded: [] });
  }

  stop(): void {
    clearTimeout(this.pushTimer);
    this.detach?.();
    this.detach = undefined;
  }
}
